import { FolderOpen } from 'lucide-react';
import * as React from 'react';
import { Navigate, useNavigate } from 'react-router';
import { api } from '../api.ts';
import { projectPath } from '../nav/paths.ts';
import { getUiView } from '../state/ui-view.ts';

const LAST_PROJECT_KEY = 'cortex_last_project';

export function rememberProject(slug: string, branch: string) {
  try {
    localStorage.setItem(LAST_PROJECT_KEY, JSON.stringify({ slug, branch }));
  } catch {
    /* not remembered */
  }
}

function lastProject(): { slug: string; branch: string } | null {
  try {
    const v = JSON.parse(localStorage.getItem(LAST_PROJECT_KEY) || 'null');
    return v && typeof v.slug === 'string' ? v : null;
  } catch {
    return null;
  }
}

const homePage = () => (getUiView() === 'simple' ? 'apps' : 'dashboard');

type Listed = { slug: string; archivedAt: string | null };
type SharedApp = { project: string; app: string; title: string; link: string };

/** Where "/" goes: the last project if it is still open to the caller, else their first project. */
export function landingTarget(projects: Listed[], last: { slug: string; branch: string } | null) {
  const open = projects.filter((p) => !p.archivedAt);
  const again = last && open.find((p) => p.slug === last.slug);
  if (again) return projectPath(homePage(), again.slug, last.branch);
  const first = open[0];
  return first ? projectPath(homePage(), first.slug, 'main') : null;
}

/**
 * Project-less paths ("/"): back to the last opened project, else the first project the caller can
 * open (its Dashboard, or Apps in the Simple view). Someone without any project is told so, and can
 * still open one by its slug.
 */
export function Landing() {
  const navigate = useNavigate();
  const [slug, setSlug] = React.useState('');
  const [target, setTarget] = React.useState<string | null | undefined>(undefined);
  const [apps, setApps] = React.useState<SharedApp[]>([]);
  React.useEffect(() => {
    let alive = true;
    const last = lastProject();
    api
      .GET('/api/me/projects')
      .then(({ data }) => alive && setTarget(data ? landingTarget(data.projects, last) : null))
      .catch(() => alive && setTarget(null));
    // Apps shared with them in projects they cannot open themselves (those projects are not theirs to list).
    api
      .GET('/api/me/apps')
      .then(({ data }) => alive && setApps(data?.apps ?? []))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  if (target === undefined) return <div className="min-h-screen bg-page-bg" data-testid="landing-loading" />;
  if (target) return <Navigate to={target} replace />;
  return (
    <div className="min-h-screen flex items-center justify-center bg-page-bg p-6" data-testid="landing">
      <form
        className="max-w-md w-full rounded-[10px] border border-card-border bg-card-bg py-10 px-8 flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (slug.trim()) navigate(projectPath(homePage(), slug.trim(), 'main'));
        }}
      >
        <FolderOpen size={32} className="text-page-muted" />
        <h1 className="text-xl font-medium text-page-title">No projects yet</h1>
        <p className="text-sm text-page-muted">
          A project appears here once an organization admin creates it or someone shares it with you. You can
          also open one by its slug.
        </p>
        {apps.length > 0 && (
          <div className="flex flex-col gap-1" data-testid="landing-shared-apps">
            <span className="text-sm font-medium text-page-title">Apps shared with you</span>
            {apps.map((a) => (
              <a
                key={`${a.project}/${a.app}`}
                href={a.link}
                className="text-sm text-nav-active hover:underline no-underline"
              >
                {a.title}
              </a>
            ))}
          </div>
        )}
        <input
          aria-label="Project slug"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="project-slug"
          className="h-9 px-3 rounded-[5px] border text-sm outline-none bg-card-bg border-[var(--input-border)] text-page-text focus:border-nav-active"
        />
        <button
          type="submit"
          className="h-9 rounded-[5px] text-sm font-medium text-white cursor-pointer border-0 bg-nav-active hover:bg-nav-active-dark transition-colors"
        >
          Open
        </button>
      </form>
    </div>
  );
}
